/**
 * WGS84 latitude/longitude → ISN93, and parsing whatever you pasted.
 *
 * The world's coordinates *are* ISN93 metres (EPSG:3057) — a game position is
 * a real Icelandic national-grid coordinate, with no projection layer. But
 * every web map on earth hands you WGS84 degrees, so pasting a position from
 * Google Maps, ja.is or map.is has to work or the coordinate box is a box you
 * cannot use without doing arithmetic first.
 *
 * ⚠️ **One direction only, deliberately.** Nothing needs ISN93 → lat/lon yet,
 * and the inverse requires an iterative solve for latitude that would be code
 * with no caller and no way to notice it had rotted.
 */

// ── EPSG:3057 — ISN93 / Lambert 1993 ────────────────────────────────────────
//
// Lambert Conformal Conic, two standard parallels, on GRS80. These constants
// are the projection's definition; changing one silently moves all of Iceland.

/** GRS80 semi-major axis, metres. */
const A = 6378137;
/** GRS80 inverse flattening. */
const INV_F = 298.257222101;
const F = 1 / INV_F;
/** First eccentricity. */
const ECC = Math.sqrt(F * (2 - F));

const LAT_ORIGIN = deg(65);
const LON_ORIGIN = deg(-19);
const STD_PARALLEL_1 = deg(64.25);
const STD_PARALLEL_2 = deg(65.75);
const FALSE_EASTING = 500000;
const FALSE_NORTHING = 500000;

function deg(d: number): number {
  return (d * Math.PI) / 180;
}

/** The conic's scale factor at a latitude. */
function m(lat: number): number {
  const s = Math.sin(lat);
  return Math.cos(lat) / Math.sqrt(1 - ECC * ECC * s * s);
}

/** The conic's isometric-latitude term. */
function t(lat: number): number {
  const s = Math.sin(lat);
  return Math.tan(Math.PI / 4 - lat / 2) / Math.pow((1 - ECC * s) / (1 + ECC * s), ECC / 2);
}

/* Computed once: they depend only on the projection's constants. */
const M1 = m(STD_PARALLEL_1);
const M2 = m(STD_PARALLEL_2);
const T1 = t(STD_PARALLEL_1);
const T2 = t(STD_PARALLEL_2);
/** Cone constant. */
const N = (Math.log(M1) - Math.log(M2)) / (Math.log(T1) - Math.log(T2));
const BIG_F = M1 / (N * Math.pow(T1, N));
const R_ORIGIN = A * BIG_F * Math.pow(t(LAT_ORIGIN), N);

/** An ISN93 position: easting and northing in metres. */
export interface Isn93 {
  readonly x: number;
  /** ⚠️ ISN93 **northing**, not the world's z. World z is the negated northing. */
  readonly y: number;
}

/**
 * Convert WGS84 degrees to ISN93 metres.
 *
 * Verified against PostGIS `ST_Transform(…, 4326 → 3057)` on five points
 * spanning the country — see `latlon.test.ts`. Agreement is sub-millimetre,
 * which is what "the same projection" should mean.
 */
export function latLonToIsn93(lat: number, lon: number): Isn93 {
  const latR = deg(lat);
  const lonR = deg(lon);

  const r = A * BIG_F * Math.pow(t(latR), N);
  const theta = N * (lonR - LON_ORIGIN);

  return {
    x: FALSE_EASTING + r * Math.sin(theta),
    y: FALSE_NORTHING + R_ORIGIN - r * Math.cos(theta),
  };
}

// ── Parsing what somebody pasted ────────────────────────────────────────────

/**
 * Iceland's extent in ISN93, generously bounded.
 *
 * Used only to tell a *coordinate* from a *typo*, so the edges are loose. The
 * real land runs roughly E 240,000–780,000 and N 290,000–710,000.
 */
const ISN93_X_RANGE = [100000, 900000] as const;
const ISN93_Y_RANGE = [200000, 800000] as const;

/** Iceland in degrees, equally generously bounded. */
const LAT_RANGE = [62.5, 67.5] as const;
const LON_RANGE = [-25.5, -12.5] as const;

export interface ParsedCoordinate {
  readonly isn93: Isn93;
  /** Which format it turned out to be — shown back so a misread is visible. */
  readonly source: 'isn93' | 'latlon';
  /** True when the two numbers arrived the other way round and were swapped. */
  readonly swapped: boolean;
}

/**
 * Pull two numbers out of a string, tolerating how people actually paste.
 *
 * ⚠️ **Decimal commas are handled.** Under an Icelandic locale a coordinate
 * copied off an Icelandic site reads `64,0709, -21,9549`, and this project has
 * already lost an afternoon to `"3.0"` parsing as `30` under is-IS. Four
 * comma-separated numeric groups mean decimal commas, not four coordinates.
 */
function twoNumbers(input: string): [number, number] | null {
  const text = input.trim();
  if (text === '') return null;

  /* `64,0709, -21,9549` and `64,0709 -21,9549` — decimal commas. */
  const decimalComma = /^(-?\d+),(\d+)[;,\s]+(-?\d+),(\d+)$/.exec(text);
  if (decimalComma) {
    const a = Number(`${decimalComma[1]}.${decimalComma[2]}`);
    const b = Number(`${decimalComma[3]}.${decimalComma[4]}`);
    return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
  }

  /* Everything else: split on commas, semicolons and whitespace. */
  const parts = text.split(/[;,\s]+/).filter((p) => p !== '');
  if (parts.length !== 2) return null;

  const a = Number(parts[0]);
  const b = Number(parts[1]);
  return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
}

function within(value: number, range: readonly [number, number]): boolean {
  return value >= range[0] && value <= range[1];
}

/**
 * Work out what somebody pasted into the coordinate box, and where it is.
 *
 * Accepts ISN93 easting/northing and WGS84 latitude/longitude, and tells them
 * apart by magnitude — which is unambiguous here rather than merely likely.
 * An ISN93 coordinate in Iceland is six figures; a latitude is never above 90.
 * The ranges do not come close to overlapping.
 *
 * Returns `null` rather than guessing when it cannot tell. A teleport to a
 * misread coordinate lands you in the sea with no clue why.
 */
export function parseCoordinate(input: string): ParsedCoordinate | null {
  const pair = twoNumbers(input);
  if (!pair) return null;
  const [a, b] = pair;

  /* Lat/lon first: the tighter test, and the one with a swap case. */
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) {
    if (within(a, LAT_RANGE) && within(b, LON_RANGE)) {
      return { isn93: latLonToIsn93(a, b), source: 'latlon', swapped: false };
    }
    /* `-21.95, 64.07` — longitude first. Some tools emit it that way, and in
       Iceland the two are never confusable: latitude is positive and in the
       sixties, longitude is negative. */
    if (within(b, LAT_RANGE) && within(a, LON_RANGE)) {
      return { isn93: latLonToIsn93(b, a), source: 'latlon', swapped: true };
    }
    return null;
  }

  if (within(a, ISN93_X_RANGE) && within(b, ISN93_Y_RANGE)) {
    return { isn93: { x: a, y: b }, source: 'isn93', swapped: false };
  }

  /* ⚠️ **No northing-first swap for ISN93, deliberately.** There is no way to
     detect one: Iceland's northing range sits entirely inside its easting
     range, so a flipped pair reads as a perfectly valid point somewhere else
     in the country. A first attempt did have that branch and it was
     unreachable — the test that proved it is in `latlon.test.ts`.

     Easting-then-northing is the convention everywhere this project writes a
     coordinate, including the HUD, so the input matches the output. The
     lat/lon swap above is kept because there it *is* unambiguous: in Iceland
     latitude is positive and longitude is negative. */
  return null;
}
